import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Curated country-code list — covers 95% of realistic B2B enquiries.
 *  Users can still type in the "Country" field freely. */
const DIAL_CODES = [
  ["+91", "India"], ["+1", "USA / Canada"], ["+44", "UK"], ["+61", "Australia"],
  ["+971", "UAE"], ["+65", "Singapore"], ["+49", "Germany"], ["+33", "France"],
  ["+81", "Japan"], ["+86", "China"], ["+82", "Korea"], ["+64", "New Zealand"],
  ["+27", "South Africa"], ["+55", "Brazil"], ["+52", "Mexico"], ["+34", "Spain"],
  ["+39", "Italy"], ["+31", "Netherlands"], ["+41", "Switzerland"], ["+46", "Sweden"],
  ["+45", "Denmark"], ["+47", "Norway"], ["+90", "Turkey"], ["+62", "Indonesia"],
  ["+63", "Philippines"], ["+66", "Thailand"], ["+60", "Malaysia"], ["+92", "Pakistan"],
  ["+880", "Bangladesh"], ["+94", "Sri Lanka"], ["+977", "Nepal"], ["+20", "Egypt"],
  ["+254", "Kenya"], ["+234", "Nigeria"], ["+966", "Saudi Arabia"], ["+972", "Israel"],
];

// Per-plan copy shown on this page. All plans hit the same backend endpoint
// (`/enquiries/empire`) — we just differentiate the `source` value so the
// operator can see in their inbox which tier the prospect came from.
const PLAN_COPY = {
  empire: {
    kicker: "Empire · Custom",
    heading: "Tell us about your business.",
    blurb:
      "Empire is priced per engagement — multi-location, white label, custom domains, priority support. " +
      "Share a few details and a KAIROSA Team Member will reply personally within 48 hours.",
    source: "landing:empire",
    subjectSuffix: "Empire",
  },
  studio: {
    kicker: "Studio · Talk to Sales",
    heading: "Tell us about your business.",
    blurb:
      "Studio is priced per business — usually one or two-location wellness operators. Share a few details " +
      "about what you're building and a KAIROSA Team Member will reply personally within 48 hours with a fit-for-purpose plan.",
    source: "landing:studio",
    subjectSuffix: "Studio",
  },
};

export default function EmpireEnquiry() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const planParam = (params.get("plan") || "empire").toLowerCase();
  const copy = PLAN_COPY[planParam] || PLAN_COPY.empire;
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    country_code: "+91",
    contact_number: "",
    email: "",
    country: "",
    description: "",
    other_info: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.email || !form.country || form.description.length < 10) {
      toast.error("Please fill name, email, country and a brief description.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/enquiries/empire", {
        ...form,
        source: copy.source,
      });
      setDone(true);
      toast.success("Received! A KAIROSA team member will reply within 48 hours.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit enquiry.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-6">
        <div className="max-w-md text-center">
          <CheckCircle2 className="h-14 w-14 text-primary mx-auto" />
          <h1 className="font-display text-4xl mt-6">Thank you.</h1>
          <p className="text-muted-foreground mt-3">
            Your {copy.subjectSuffix} enquiry is on its way. A KAIROSA team member will reply personally to{" "}
            <span className="text-foreground font-medium">{form.email}</span> within 48 hours (usually much sooner).
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/"><Button variant="outline" className="rounded-full" data-testid="empire-done-home">Back to home</Button></Link>
            <Link to="/register?plan=solo"><Button className="rounded-full" data-testid="empire-done-solo">Or try Solo free</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-14">
        <Link
          to="/"
          data-testid="empire-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>

        <div className="mt-8 flex items-center gap-3">
          <Building2 className="h-8 w-8 text-primary" />
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground" data-testid="enquiry-kicker">{copy.kicker}</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-3">{copy.heading}</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">
          {copy.blurb}
        </p>

        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Company name <span className="opacity-50">(optional)</span></label>
            <input data-testid="empire-company" value={form.company_name} onChange={upd("company_name")} className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-3 text-sm" placeholder="Your Wellness Studio Pvt. Ltd." />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Contact name *</label>
              <input required data-testid="empire-name" value={form.contact_name} onChange={upd("contact_name")} className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-3 text-sm" placeholder="Full name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Email *</label>
              <input required type="email" data-testid="empire-email" value={form.email} onChange={upd("email")} className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-3 text-sm" placeholder="you@brand.com" />
            </div>
          </div>

          <div className="grid sm:grid-cols-[130px_1fr] gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Code</label>
              <select data-testid="empire-country-code" value={form.country_code} onChange={upd("country_code")} className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-2 text-sm">
                {DIAL_CODES.map(([code, label]) => (
                  <option key={code + label} value={code}>{code} · {label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Contact number <span className="opacity-50">(optional)</span></label>
              <input data-testid="empire-phone" value={form.contact_number} onChange={upd("contact_number")} className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-3 text-sm" placeholder="98765 43210" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Country *</label>
            <input required data-testid="empire-country" value={form.country} onChange={upd("country")} className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-3 text-sm" placeholder="India" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Problem & intended usage *</label>
            <textarea required data-testid="empire-description" value={form.description} onChange={upd("description")} rows={5} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder="Tell us about your current setup, pain points and what you're hoping Kairosa can solve…" />
            <div className="text-[10px] mt-1 text-muted-foreground">{form.description.length} / 4000 characters</div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Anything else? <span className="opacity-50">(optional)</span></label>
            <textarea data-testid="empire-other" value={form.other_info} onChange={upd("other_info")} rows={3} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder="Locations, team size, integrations, timeline…" />
          </div>

          <Button data-testid="empire-submit" type="submit" disabled={busy} className="h-12 px-8 rounded-full">
            <Send className="h-4 w-4 mr-2" />
            {busy ? "Sending…" : "Send enquiry"}
          </Button>
          <div className="text-[11px] text-muted-foreground">By submitting you agree to be contacted about your enquiry. We never share your details.</div>
        </form>
      </div>
    </div>
  );
}
